// frontend/src/admin/pages/AdminFiscal.js - DASHBOARD FISCAL REACT
import React, { useState, useEffect } from 'react';
import '../styles/AdminFiscal.css';

// ✅ utilisation du client Axios commun
import api from '../../services/api';

const AdminFiscal = ({ adminUser, onPageChange }) => {
  const [fiscalData, setFiscalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFiscalData = async () => {
    try {
      setError(null);
      setLoading(true);

      // 👉 plus de fetch('http://localhost:5000/...') :
      const response = await api.get('/admin/fiscal/dashboard');
      const data = response.data;

      if (data && data.success) {
        setFiscalData(data.fiscal);
      } else {
        setError(data?.message || 'Erreur lors du chargement des données fiscales');
      }
    } catch (err) {
      console.error('Erreur dashboard fiscal:', err);
      setError('Impossible de se connecter au serveur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // synchro avec la sidebar
    onPageChange?.('fiscal');

    fetchFiscalData();

    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(fetchFiscalData, 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !fiscalData) {
    return (
      <div className="admin-fiscal-loading">
        <div className="admin-loading-spinner"></div>
        <h2>📊 Chargement des données fiscales...</h2>
      </div>
    );
  }

  if (error && !fiscalData) {
    return (
      <div className="admin-fiscal-error">
        <h2>❌ {error}</h2>
        <button onClick={fetchFiscalData} className="admin-btn admin-btn--primary">
          🔄 Réessayer
        </button>
      </div>
    );
  }

  if (!fiscalData) return null;

  const {
    chiffre_affaires,
    seuils,
    cotisations,
    impots,
    synthese_mensuelle,
    alertes,
    abonnes_maximum,
    comparaison_regimes,
    echeances,
    conseils,
  } = fiscalData;

  return (
    <div className="admin-fiscal">
      {/* Header */}
      <div className="admin-fiscal__header">
        <div className="admin-fiscal__title">
          <h1>💼 Dashboard Fiscal Auto-Entrepreneur</h1>
          <p>Suivi en temps réel de votre situation fiscale et recommandations personnalisées</p>
        </div>
        <button onClick={fetchFiscalData} className="admin-btn admin-btn--primary">
          🔄 Actualiser
        </button>
      </div>

      {/* ALERTES CRITIQUES */}
      {alertes && alertes.length > 0 && (
        <div className="admin-fiscal-alerts">
          <h2>🚨 Alertes Fiscales</h2>
          <div className="admin-fiscal-alerts__list">
            {alertes.map((alerte, index) => (
              <div
                key={index}
                className={`admin-fiscal-alert admin-fiscal-alert--${alerte.type}`}
              >
                <div className="admin-fiscal-alert__icon">{alerte.icon}</div>
                <div className="admin-fiscal-alert__content">
                  <h3>{alerte.titre}</h3>
                  <p>{alerte.message}</p>
                  <div className="admin-fiscal-alert__action">
                    <strong>Action à prendre :</strong> {alerte.action}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SYNTHÈSE MENSUELLE DÉTAILLÉE */}
      <div className="admin-fiscal-section">
        <h2>💰 Synthèse Mensuelle (Ce mois)</h2>
        <div className="admin-fiscal-calcul-detaille">
          <div className="admin-fiscal-calcul-card">
            <div className="admin-fiscal-calcul-header">
              <h3>📊 Calcul Détaillé des Charges</h3>
              <span className="admin-fiscal-badge">
                Taux total : {synthese_mensuelle.taux_charges}
              </span>
            </div>

            <div className="admin-fiscal-calcul-table">
              {synthese_mensuelle.calcul_detaille.map((ligne, index) => (
                <div
                  key={index}
                  className={`admin-fiscal-calcul-row ${
                    ligne.ligne === '---' ? 'separator' : ''
                  } ${ligne.ligne.includes('TOTAL') ? 'total' : ''} ${
                    ligne.ligne.includes('RESTE') ? 'final' : ''
                  }`}
                >
                  <span className="admin-fiscal-calcul-label">{ligne.ligne}</span>
                  <span className="admin-fiscal-calcul-montant">
                    {ligne.montant !== '---' && `${ligne.montant} €`}
                  </span>
                </div>
              ))}
            </div>

            <div className="admin-fiscal-calcul-footer">
              <div className="admin-fiscal-info-box">
                <span className="label">💵 CA mensuel</span>
                <span className="value">{synthese_mensuelle.ca_mensuel} €</span>
              </div>
              <div className="admin-fiscal-info-box">
                <span className="label">📉 Charges totales</span>
                <span className="value red">{synthese_mensuelle.total_charges} €</span>
              </div>
              <div className="admin-fiscal-info-box highlight">
                <span className="label">✅ Revenu net</span>
                <span className="value green">{synthese_mensuelle.revenu_net} €</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CHIFFRE D'AFFAIRES */}
      <div className="admin-fiscal-section">
        <h2>📈 Chiffre d'Affaires</h2>
        <div className="admin-fiscal-grid">
          <div className="admin-fiscal-card">
            <div className="admin-fiscal-card__label">Ce mois</div>
            <div className="admin-fiscal-card__value">{chiffre_affaires.ce_mois} €</div>
            <div className="admin-fiscal-card__detail">
              {chiffre_affaires.nb_factures_mois} factures
            </div>
          </div>

          <div className="admin-fiscal-card">
            <div className="admin-fiscal-card__label">Cette année</div>
            <div className="admin-fiscal-card__value primary">
              {parseFloat(chiffre_affaires.cette_annee).toLocaleString()} €
            </div>
            <div className="admin-fiscal-card__detail">
              {chiffre_affaires.nb_factures_annee} factures
            </div>
          </div>

          <div className="admin-fiscal-card">
            <div className="admin-fiscal-card__label">CA moyen/mois</div>
            <div className="admin-fiscal-card__value">
              {parseFloat(chiffre_affaires.ca_moyen_mensuel).toLocaleString()} €
            </div>
          </div>

          <div className="admin-fiscal-card">
            <div className="admin-fiscal-card__label">Projection fin d'année</div>
            <div className="admin-fiscal-card__value warning">
              {parseFloat(chiffre_affaires.projection_fin_annee).toLocaleString()} €
            </div>
          </div>
        </div>
      </div>

      {/* SEUILS FISCAUX */}
      <div className="admin-fiscal-section">
        <h2>🎯 Seuils Fiscaux 2025</h2>
        <div className="admin-fiscal-seuils">
          {/* Seuil Auto-Entrepreneur */}
          <div className="admin-fiscal-seuil">
            <div className="admin-fiscal-seuil__header">
              <h3>🔴 Seuil Auto-Entrepreneur</h3>
              <span className="admin-fiscal-seuil__limit">
                {seuils.ca_max_ae.toLocaleString()} €
              </span>
            </div>
            <div className="admin-fiscal-progress">
              <div
                className="admin-fiscal-progress__bar"
                style={{
                  width: `${seuils.pourcentage_seuil_ae}%`,
                  backgroundColor:
                    parseFloat(seuils.pourcentage_seuil_ae) > 80
                      ? '#ef4444'
                      : parseFloat(seuils.pourcentage_seuil_ae) > 60
                      ? '#f59e0b'
                      : '#10b981',
                }}
              ></div>
            </div>
            <div className="admin-fiscal-seuil__stats">
              <span>Utilisé : {seuils.pourcentage_seuil_ae}%</span>
              <span>
                Restant :{' '}
                {parseFloat(seuils.restant_avant_seuil_ae).toLocaleString()} €
              </span>
            </div>
          </div>

          {/* Seuil TVA */}
          <div className="admin-fiscal-seuil">
            <div className="admin-fiscal-seuil__header">
              <h3>💶 Franchise TVA</h3>
              <span className="admin-fiscal-seuil__limit">
                {seuils.tva_franchise.toLocaleString()} €
              </span>
            </div>
            <div className="admin-fiscal-progress">
              <div
                className="admin-fiscal-progress__bar tva"
                style={{
                  width: `${seuils.pourcentage_seuil_tva}%`,
                  backgroundColor:
                    parseFloat(seuils.pourcentage_seuil_tva) > 90
                      ? '#ef4444'
                      : parseFloat(seuils.pourcentage_seuil_tva) > 70
                      ? '#f59e0b'
                      : '#3b82f6',
                }}
              ></div>
            </div>
            <div className="admin-fiscal-seuil__stats">
              <span>Utilisé : {seuils.pourcentage_seuil_tva}%</span>
              <span>
                Restant :{' '}
                {parseFloat(seuils.restant_avant_seuil_tva).toLocaleString()} €
              </span>
            </div>
            <div className="admin-fiscal-seuil__warning">
              ⚠️ Tolérance année 1 : {seuils.tva_tolerance.toLocaleString()} €
            </div>
          </div>
        </div>
      </div>

      {/* COTISATIONS & IMPÔTS */}
      <div className="admin-fiscal-section">
        <h2>💸 Cotisations & Impôts Mensuels</h2>
        <div className="admin-fiscal-grid three-cols">
          <div className="admin-fiscal-card highlight">
            <div className="admin-fiscal-card__label">Cotisations URSSAF</div>
            <div className="admin-fiscal-card__value red">
              {cotisations.detail_mensuel.urssaf} €
            </div>
            <div className="admin-fiscal-card__detail">
              Taux : {cotisations.taux_urssaf}
            </div>
          </div>

          <div className="admin-fiscal-card highlight">
            <div className="admin-fiscal-card__label">
              Impôts (
              {impots.recommandation === 'versement_liberatoire' ? 'VL' : 'IR'})
            </div>
            <div className="admin-fiscal-card__value orange">
              {impots.montant_recommande} €
            </div>
            <div className="admin-fiscal-card__detail">Taux : {impots.taux_vl}</div>
          </div>

          <div className="admin-fiscal-card highlight">
            <div className="admin-fiscal-card__label">Formation Pro</div>
            <div className="admin-fiscal-card__value">
              {cotisations.detail_mensuel.formation} €
            </div>
            <div className="admin-fiscal-card__detail">0.1% du CA</div>
          </div>
        </div>

        <div className="admin-fiscal-projection">
          <h3>📊 Projection Annuelle</h3>
          <div className="admin-fiscal-projection__values">
            <div className="admin-fiscal-projection__item">
              <span>Cotisations</span>
              <strong>
                {parseFloat(cotisations.projection_annuelle).toLocaleString()} €
              </strong>
            </div>
            <div className="admin-fiscal-projection__item">
              <span>Impôts</span>
              <strong>
                {parseFloat(impots.projection_annuelle).toLocaleString()} €
              </strong>
            </div>
            <div className="admin-fiscal-projection__item total">
              <span>Total charges annuelles</span>
              <strong>
                {(
                  parseFloat(cotisations.projection_annuelle) +
                  parseFloat(impots.projection_annuelle)
                ).toLocaleString()}{' '}
                €
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* ABONNÉS MAXIMUM */}
      <div className="admin-fiscal-section">
        <h2>🎯 Abonnés Maximum (Auto-Entrepreneur)</h2>
        <div className="admin-fiscal-abonnes">
          {abonnes_maximum.map((plan, index) => (
            <div key={index} className="admin-fiscal-abonne-card">
              <div className="admin-fiscal-abonne-card__header">
                <h3>{plan.plan_name}</h3>
                <span className="price">{plan.prix_mensuel} € / mois</span>
              </div>

              <div className="admin-fiscal-abonne-card__stats">
                <div className="stat">
                  <span className="label">Actuels</span>
                  <span className="value">{plan.nb_actuel}</span>
                </div>
                <div className="stat">
                  <span className="label">Maximum AE</span>
                  <span className="value">{plan.max_abonnes_ae}</span>
                </div>
                <div className="stat">
                  <span className="label">Places restantes</span>
                  <span className="value green">{plan.marge_restante}</span>
                </div>
              </div>

              <div className="admin-fiscal-progress">
                <div
                  className="admin-fiscal-progress__bar"
                  style={{
                    width: `${plan.pourcentage_utilise}%`,
                    backgroundColor:
                      parseFloat(plan.pourcentage_utilise) > 80
                        ? '#ef4444'
                        : parseFloat(plan.pourcentage_utilise) > 60
                        ? '#f59e0b'
                        : '#10b981',
                  }}
                ></div>
              </div>
              <div className="admin-fiscal-abonne-card__footer">
                Utilisation : {plan.pourcentage_utilise}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* COMPARAISON RÉGIMES */}
      <div className="admin-fiscal-section">
        <h2>⚖️ Comparaison des Régimes Fiscaux</h2>
        <div className="admin-fiscal-comparaison">
          {Object.entries(comparaison_regimes)
            .filter(([key]) => key !== 'recommandation')
            .map(([key, regime]) => (
              <div
                key={key}
                className={`admin-fiscal-regime ${
                  comparaison_regimes.recommandation === key ? 'recommande' : ''
                }`}
              >
                {comparaison_regimes.recommandation === key && (
                  <div className="admin-fiscal-regime__badge">⭐ RECOMMANDÉ</div>
                )}

                <h3>{regime.nom}</h3>

                <div className="admin-fiscal-regime__stats">
                  <div className="stat">
                    <span>CA mensuel</span>
                    <strong>
                      {parseFloat(regime.ca_mensuel).toLocaleString()} €
                    </strong>
                  </div>
                  <div className="stat">
                    <span>Charges</span>
                    <strong className="red">
                      {parseFloat(regime.charges_totales).toLocaleString()} €
                    </strong>
                  </div>
                  <div className="stat highlight">
                    <span>Revenu net</span>
                    <strong className="green">
                      {parseFloat(regime.revenu_net).toLocaleString()} €
                    </strong>
                  </div>
                  <div className="stat">
                    <span>Taux charges</span>
                    <strong>{regime.taux_charges}</strong>
                  </div>
                </div>

                <div className="admin-fiscal-regime__details">
                  <div className="avantages">
                    <h4>✅ Avantages</h4>
                    <ul>
                      {regime.avantages.map((av, i) => (
                        <li key={i}>{av}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="inconvenients">
                    <h4>❌ Inconvénients</h4>
                    <ul>
                      {regime.inconvenients.map((inc, i) => (
                        <li key={i}>{inc}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* PROCHAINES ÉCHÉANCES */}
      <div className="admin-fiscal-section">
        <h2>📅 Prochaines Échéances</h2>
        <div className="admin-fiscal-echeances">
          {Object.entries(echeances).map(([key, echeance]) => (
            <div key={key} className="admin-fiscal-echeance-card">
              <div className="admin-fiscal-echeance-card__date">
                {new Date(echeance.date).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </div>
              <div className="admin-fiscal-echeance-card__content">
                <h4>{echeance.description}</h4>
                <div className="montant">
                  Montant estimé :{' '}
                  <strong>
                    {parseFloat(echeance.montant_estime).toLocaleString('fr-FR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}{' '}
                    €
                  </strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONSEILS PERSONNALISÉS */}
      {conseils && conseils.length > 0 && (
        <div className="admin-fiscal-section">
          <h2>💡 Conseils Personnalisés</h2>
          <div className="admin-fiscal-conseils">
            {conseils.map((conseil, index) => (
              <div
                key={index}
                className={`admin-fiscal-conseil admin-fiscal-conseil--${conseil.type}`}
              >
                <h4>{conseil.titre}</h4>
                <p>{conseil.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFiscal;
